import { useInView } from 'motion/react';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { useYoutubeVideo } from '@/providers/YoutubeVideoContext';
import { YTRef } from '../YoutubeEmbed/YoutubeEmbed';

interface BasicEmbedProps {
  videoUrl: string;
  className?: string;
}

const BasicEmbed = forwardRef<YTRef, BasicEmbedProps>(({ videoUrl, className }, ref) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const inView = useInView(videoRef, { amount: 0.5 });
  const { video, setCurrentVideo } = useYoutubeVideo();
  const playVideo = () => videoRef.current?.play();
  const pauseVideo = () => videoRef.current?.pause();
  const mute = () => videoRef.current && (videoRef.current.muted = true);
  const unMute = () => videoRef.current && (videoRef.current.muted = false);
  const setVolume = (volume: number) => {
    if (videoRef.current) {
      videoRef.current.volume = Math.max(0, Math.min(1, volume));
    }
  };
  const getVolume = () => videoRef.current?.volume ?? 0;
  const onPlay = () => {
    video?.pauseVideo();
    setCurrentVideo({ playVideo, pauseVideo, mute, unMute, setVolume, getVolume });
  }
  const onPause = () => setCurrentVideo(null);
  useImperativeHandle(ref, () => ({
    getPlayer: () => ({ playVideo, pauseVideo, mute, unMute, setVolume, getVolume }),
  }));
  useEffect(() => {
    if (!inView) pauseVideo();
  }, [inView]);
  useEffect(() => {
    videoRef.current?.addEventListener('play', onPlay);
    videoRef.current?.addEventListener('pause', onPause);
    return () => {
      videoRef.current?.removeEventListener('play', onPlay);
      videoRef.current?.removeEventListener('pause', onPause);
    };
  }, []);
  return (
    <video controls ref={videoRef} className={className}>
      <source src={videoUrl} type="video/mp4" />
    </video>
  )
});

export default BasicEmbed;